/**
 * Pure utility functions extracted from useDashboardData for testability.
 *
 * These handle the core data normalization logic:
 * - Apple Health batch deduplication (multiple sync batches per date)
 * - Body composition delta computation (latest - earliest)
 * - Prior-date value extraction (exclude today's partial data)
 */

// ── Batch dedup (Apple Health) ──────────────────────────────────────────────
// Multiple syncs create near-duplicate rows with slightly different
// recorded_at timestamps. Group by (date, MM:SS suffix) to identify
// sync batches, sum each batch, then take the largest per date.

export type BatchMap = Record<string, Record<string, number>>; // date → mmss → sum

export function addToBatch(bm: BatchMap, date: string, mmss: string, value: number): void {
  if (!bm[date]) bm[date] = {};
  bm[date][mmss] = (bm[date][mmss] || 0) + value;
}

/** Resolve deduped daily values: for each date, take the largest batch sum. */
export function bestBatchPerDay(bm: BatchMap): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [date, batches] of Object.entries(bm)) {
    out[date] = Object.values(batches).reduce((a, b) => Math.max(a, b), -Infinity);
  }
  return out;
}

// ── Body composition deltas ─────────────────────────────────────────────────

/** Compute delta between earliest and latest value, rounded to 1 decimal. Returns null if < 2 data points. */
export function computeDelta(map: Record<string, number>): number | null {
  const dates = Object.keys(map).sort();
  if (dates.length < 2) return null;
  return Math.round((map[dates[dates.length - 1]] - map[dates[0]]) * 10) / 10;
}

// ── Prior-or-latest extraction ──────────────────────────────────────────────

/**
 * Return the value from the most recent date strictly before `today`.
 *
 * Fallback: if no prior date exists (sparse accounts with only same-day or
 * future data), returns the latest value in the map. This means first-day
 * users see a real number instead of "—" for fields like RHR / weight / BMI.
 * Callers expecting strict prior-only semantics should null-check the date.
 */
export function latestOrPriorTo(map: Record<string, number>, today: string): number | null {
  const sorted = Object.keys(map).sort();
  if (!sorted.length) return null;
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (sorted[i] < today) return map[sorted[i]];
  }
  return map[sorted[sorted.length - 1]];
}

// ── Lab dedup ───────────────────────────────────────────────────────────────

/** Dedup labs: keep only the latest per normalized marker (assumes rows are sorted newest-first). */
export function dedupLabs<T extends { marker: string }>(
  rows: T[],
  keySet: Set<string>,
  normalize: (marker: string) => string,
): T[] {
  const seen = new Set<string>();
  const latest: T[] = [];
  for (const row of rows) {
    const canonical = normalize(row.marker);
    if (!keySet.has(canonical)) continue;
    if (!seen.has(canonical)) {
      seen.add(canonical);
      latest.push({ ...row, marker: canonical });
    }
  }
  return latest;
}
